import {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { ColumnAssignment } from '../ColumnsStep'
import { OpportunityTypeFormType } from '../OpportunityTypeStep'
import { ContactsImportResponse } from 'models/contacts'
import Papa from 'papaparse'
import { toast } from 'react-toastify'
import useApiService from 'services/api'
import { queryClient } from 'services/queryClient'
import to from 'await-to-js'

const ImportContactContext = createContext<ImportContactContextData | null>(
  null
)

export type MatchedCompany = {
  company_name: string
  matched_company?: {
    id?: number
    chain?: number
  }
}

export type ContactCampaign = { sub_category_id: number; campaign_id?: number }

export function ImportContactProvider({
  children,
  onStepChange,
  handleClose,
  onContactsImported,
}: {
  children: React.ReactNode
  onStepChange?: (step: number) => void
  handleClose: () => void
  onContactsImported?: () => void
}) {
  const api = useApiService()
  const [step, setStep] = useState(0)
  const [file, setFile] = useState<File | null>(null)
  const [tags, setTags] = useState<number[]>([])
  const [campaigns, setCampaigns] = useState<ContactCampaign[]>()
  const [parsedFile, setParsedFile] = useState<Papa.ParseResult<
    Record<string, string>
  > | null>(null)
  const [columnAssignment, setColumnAssignment] = useState<ColumnAssignment[]>(
    []
  )
  const [previewData, setPreviewData] = useState<ContactsImportResponse | null>(
    null
  )
  const [unmatchedCompanies, setUnmatchedCompanies] = useState<string[] | null>(
    null
  )
  const [isImporting, setIsImporting] = useState(false)
  const [matchedCompanies, setMatchedCompanies] = useState<MatchedCompany[]>([])
  const [opportunityType, setOpportunityType] =
    useState<OpportunityTypeFormType>({
      categoryId: undefined,
      subcategoryId: undefined,
    })

  useEffect(() => {
    if (onStepChange) onStepChange(step)
    if (step < 0) {
      handleClose()
    }
  }, [step])

  useEffect(() => {
    if (!file) return
    Papa.parse(file, {
      header: true,
      complete: (results) => {
        setParsedFile(results as Papa.ParseResult<Record<string, string>>)
      },
      error: (error) => {
        console.error(error)
      },
    })
  }, [file])

  const importContacts = async () => {
    setIsImporting(true)
    const [err, response] = await to(
      api.contactsImport(
        file!,
        columnAssignment,
        matchedCompanies,
        tags,
        campaigns?.filter((it) => it.campaign_id != undefined),
        opportunityType.subcategoryId
      )
    )
    setIsImporting(false)
    if (err) {
      toast.error('Failed to update contacts')
      return
    }

    toast.success(`${response.success_count} contacts updated successfully`)
    // Bulk imports add contacts that may join an open campaign's
    // recipient set; invalidate lead-review so the sequence wizard's
    // recipient count reflects the new contacts immediately instead
    // of waiting for launch (FIR-7242).
    void queryClient.invalidateQueries({
      queryKey: ['campaign-lead-review'],
    })
    onContactsImported?.()
    handleClose()
  }

  const nextStep = useCallback(
    (skip: number = 0) => {
      let newStep = step + 1 + skip
      if (newStep == 5 && previewData?.failed_count == 0) {
        newStep += 1
      }
      setStep(newStep)
    },
    [step, unmatchedCompanies, unmatchedCompanies?.length, previewData]
  )

  const prevStep = () => {
    let newStep = step - 1
    if (
      newStep == 3 &&
      (!unmatchedCompanies || unmatchedCompanies.length == 0)
    ) {
      newStep -= 1
    }
    if (newStep == 5 && previewData?.failed_count == 0) {
      newStep -= 1
    }
    setStep(newStep)
  }

  return (
    <ImportContactContext.Provider
      value={{
        methods: {
          nextStep,
          prevStep,
          setFile,
          setParsedFile,
          setColumnAssignment,
          setPreviewData,
          setUnmatchedCompanies,
          handleClose,
          importContacts,
          setMatchedCompanies,
          setTags,
          setCampaigns,
          setOpportunityType,
        },
        state: {
          step,
          file,
          parsedFile,
          columnAssignment,
          previewData,
          unmatchedCompanies,
          isImporting,
          tags,
          campaigns,
          opportunityType,
        },
      }}
    >
      {children}
    </ImportContactContext.Provider>
  )
}

type ImportContactContextData = {
  methods: {
    nextStep: (skip?: number) => void
    prevStep: () => void
    setFile: Dispatch<SetStateAction<File | null>>
    setParsedFile: Dispatch<
      SetStateAction<Papa.ParseResult<Record<string, string>> | null>
    >
    setColumnAssignment: Dispatch<SetStateAction<ColumnAssignment[]>>
    setPreviewData: Dispatch<SetStateAction<ContactsImportResponse | null>>
    setUnmatchedCompanies: Dispatch<SetStateAction<string[] | null>>
    handleClose: () => void
    importContacts: () => Promise<void>
    setMatchedCompanies: Dispatch<SetStateAction<MatchedCompany[]>>
    setTags: Dispatch<SetStateAction<number[]>>
    setCampaigns: Dispatch<SetStateAction<ContactCampaign[] | undefined>>
    setOpportunityType: Dispatch<SetStateAction<OpportunityTypeFormType>>
  }
  state: {
    step: number
    file: File | null
    parsedFile: Papa.ParseResult<Record<string, string>> | null
    columnAssignment: ColumnAssignment[]
    previewData: ContactsImportResponse | null
    unmatchedCompanies: string[] | null
    isImporting: boolean
    tags: number[]
    campaigns: ContactCampaign[] | undefined
    opportunityType: OpportunityTypeFormType
  }
}

export function useImportContactContext() {
  const context = useContext(ImportContactContext) as ImportContactContextData
  if (!context) {
    throw new Error(
      'useImportContactContext must be used within a ImportContactProvider'
    )
  }
  return { ...context }
}
